import { useEffect, useState, type ReactNode } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import inboxCopy, { type InboxCopy } from '@/i18n/ns/inbox';
import {
  inboxKindExtension,
  useInboxHeading,
  useInboxKindLabels,
  useInboxRowMeta,
} from './inbox-extensions';
import './apps/inbox-kinds';
import './agent-requests/inbox-kinds';
import { ActionButton } from '@/components/assistant-ui/action-button';
import { writeWorkspaceHash } from '@/lib/chat-links';
import { useWorkspace } from './context';
import { CanvasFooter } from './canvas-footer';
import { completeMarkdown, WorkspaceMarkdown } from './markdown';
import { ErrorState, LoadingState } from './primitives';
import { parseWorkspaceRoute, viewLabels } from './navigation';
import { sharingApi, type SharingRequest, type SharingRequestDetail } from './sharing-api';
import { inboxApi, useInbox, type InboxAction, type InboxItem } from './inbox-api';
import {
  dateTimeLabel,
  findInboxEntry,
  itemTitle,
  kindCopyKey,
  orderedActions,
  updateExcerpt,
  whenLabel,
  type InboxEntry,
} from './inbox-model';
import { useInboxKey } from './inbox';
import { markdownPlainText } from './plain-text';
import { useIntlLocale } from './intl-locale';

// An Inbox item in the one canvas (CANVAS-NAVIGATION; DESIGN-SPEC §7): the heading block
// names it, the body reads it as a document (model text rendered as the answer renders
// it, never as markdown source), and the footer holds its one decision. Opening another
// item replaces this one with a back path; expand focuses it; on a phone it is the sheet.

const joined = (...parts: (string | null | undefined | false)[]) =>
  parts.filter(Boolean).join(' · ');
const kindText = (copy: InboxCopy, kind: string, extended?: (kind: string) => string | null) =>
  extended?.(kind) ?? copy[kindCopyKey(kind)];

function Heading({
  meta,
  title,
  description,
}: {
  meta: string;
  title: string;
  description?: string | null;
}) {
  return (
    <header className="ws-inbox-canvas-heading">
      {meta && <p className="ws-inbox-canvas-meta">{meta}</p>}
      <h1>{title}</h1>
      {description && <p className="ws-inbox-canvas-description">{description}</p>}
    </header>
  );
}

function Footer({ sentence, children }: { sentence?: string; children?: ReactNode }) {
  if (!sentence && !children) return null;
  return (
    <CanvasFooter className="ws-inbox-canvas-footer">
      <div>{sentence && <strong>{sentence}</strong>}</div>
      {children && <div className="ws-actions">{children}</div>}
    </CanvasFooter>
  );
}

/** Follows an item's link: another object opens in this canvas (with a back path),
 * anything else is a page or chat of its own. */
function useFollow() {
  const ws = useWorkspace();
  return (href: string) => {
    const route = parseWorkspaceRoute(href);
    if (route.canvas) ws.openCanvas(route.canvas);
    else writeWorkspaceHash(href);
  };
}

// ── Updates ─────────────────────────────────────────────────────────────────────

const OPEN_LABEL: Record<string, keyof InboxCopy> = {
  agent_run: 'openRun',
  meeting_ready: 'openMeeting',
};

function UpdateView({
  entry,
  copy,
  locale,
}: {
  entry: Extract<InboxEntry, { section: 'update' }>;
  copy: InboxCopy;
  locale: string;
}) {
  const ws = useWorkspace();
  const follow = useFollow();
  const { refresh } = useInbox(useInboxKey());
  const rowMeta = useInboxRowMeta();
  const headingOf = useInboxHeading();
  const { item, group } = entry;
  const unread = group.unread.join('\u0000');
  // Opening a row reads its whole group: the latest and the earlier ones behind it.
  useEffect(() => {
    if (!group.unread.length) return;
    void inboxApi
      .markRead({ ids: group.unread })
      .then(refresh)
      .catch(() => ws.notify(copy.readFailed, null, 'error'));
  }, [unread]);
  const body = item.body ? completeMarkdown(item.body) : null;
  const others = group.items.filter((update) => update.id !== item.id);
  return (
    <div className="ws-inbox-canvas">
      <Heading
        meta={
          headingOf(item).meta ??
          joined(rowMeta(item).by ?? item.actor?.name, dateTimeLabel(item.createdAt, locale))
        }
        title={rowMeta(item).title ?? item.title}
        description={headingOf(item).description}
      />
      <div className="ws-inbox-canvas-body">
        {body?.text && (
          <div className="ws-inbox-document">
            <WorkspaceMarkdown text={body.text} />
          </div>
        )}
        {others.length > 0 && (
          <section
            className="ws-inbox-series"
            aria-label={group.latest.id === item.id ? copy.earlier : copy.others}
          >
            <h2>{group.latest.id === item.id ? copy.earlier : copy.others}</h2>
            <ul>
              {others.map((update) => (
                <li key={update.id}>
                  <button
                    type="button"
                    onClick={() => ws.openCanvas({ kind: 'inbox', id: update.id })}
                  >
                    <span>{whenLabel(update.createdAt, locale)}</span>
                    <small>{updateExcerpt(update) || rowMeta(update).title || update.title}</small>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <Footer sentence={body?.cut ? copy.shortened : undefined}>
        {item.href && (
          <ActionButton variant="primary" data-size="md" onClick={() => follow(item.href!)}>
            {copy[OPEN_LABEL[item.kind] ?? 'open']}
          </ActionButton>
        )}
      </Footer>
    </div>
  );
}

// ── Items that ask for a decision (a sharing request, …) ──────

function ItemView({
  entry,
  copy,
  locale,
}: {
  entry: Extract<InboxEntry, { section: 'needs' | 'waiting' }>;
  copy: InboxCopy;
  locale: string;
}) {
  const ws = useWorkspace();
  const follow = useFollow();
  const { refresh } = useInbox(useInboxKey());
  const [busy, setBusy] = useState(false);
  const headingOf = useInboxHeading();
  const { item } = entry;
  const extension = inboxKindExtension(item.kind);
  const { primary, others } = orderedActions(item.actions);
  async function decide(action: InboxAction) {
    setBusy(true);
    try {
      await inboxApi.act(action);
      await Promise.all([refresh(), ws.refresh()]);
      ws.notify(copy.actionDone);
      ws.openCanvas(null, { replace: true });
    } catch (failure) {
      ws.notify(failure instanceof Error ? failure.message : copy.actionFailed, null, 'error');
    } finally {
      setBusy(false);
    }
  }
  // An extension kind draws its own decision in its detail, so the footer offers none.
  const decidable = !extension && entry.section === 'needs' && item.actions.length > 0;
  return (
    <div className="ws-inbox-canvas">
      <Heading
        meta={
          headingOf(item).meta ?? joined(item.actor?.name, dateTimeLabel(item.createdAt, locale))
        }
        title={headingOf(item).title ?? itemTitle(item)}
        description={
          headingOf(item).description !== undefined
            ? headingOf(item).description
            : item.detail
              ? markdownPlainText(item.detail)
              : null
        }
      />
      <div className="ws-inbox-canvas-body">
        {extension ? (
          <extension.Detail
            item={item}
            onSettled={() => {
              void Promise.all([refresh(), ws.refresh()]);
            }}
          />
        ) : (
          <Preview item={item} copy={copy} />
        )}
      </div>
      <Footer
        sentence={
          entry.section === 'waiting'
            ? copy.waitingFooter
            : decidable
              ? copy.pendingFooter
              : undefined
        }
      >
        {decidable ? (
          <>
            {others.map((action) => (
              <ActionButton
                key={`${action.path}:${JSON.stringify(action.body)}`}
                variant="quiet"
                data-size="md"
                disabled={busy}
                onClick={() => void decide(action)}
              >
                {action.label}
              </ActionButton>
            ))}
            {!primary && item.href && (
              <ActionButton
                variant="primary"
                data-size="md"
                disabled={busy}
                onClick={() => follow(item.href!)}
              >
                {copy.open}
              </ActionButton>
            )}
            {primary && (
              <ActionButton
                variant="primary"
                data-size="md"
                busy={busy}
                disabled={busy}
                onClick={() => void decide(primary)}
              >
                {primary.label}
              </ActionButton>
            )}
          </>
        ) : (
          item.href &&
          entry.section === 'needs' && (
            <ActionButton variant="secondary" data-size="md" onClick={() => follow(item.href!)}>
              {copy.open}
            </ActionButton>
          )
        )}
      </Footer>
    </div>
  );
}

// A preview is text a person or the model wrote, read as a document.
function Preview({ item, copy }: { item: InboxItem; copy: InboxCopy }) {
  return (
    <>
      {item.preview && (
        <div className="ws-inbox-document">
          <WorkspaceMarkdown text={item.preview} />
        </div>
      )}
      {item.previewComplete === false && <p className="ws-inbox-note">{copy.tooLong}</p>}
    </>
  );
}

// ── Sharing requests: a Skill to publish, a share, a Result revision ─────────────

function useSharingRequest(requestId: string, copy: InboxCopy) {
  const [reload, setReload] = useState(0);
  const key = `${requestId}\u0000${reload}`;
  const [state, setState] = useState<{
    key: string;
    detail: SharingRequestDetail | null;
    error: string;
  }>({ key: '', detail: null, error: '' });
  useEffect(() => {
    let active = true;
    void sharingApi
      .read(requestId)
      .then((detail) => active && setState({ key, detail, error: '' }))
      .catch(
        (failure) =>
          active &&
          setState({
            key,
            detail: null,
            error: failure instanceof Error ? failure.message : copy.openFailed,
          }),
      );
    return () => {
      active = false;
    };
  }, [key]);
  return { ...state, loading: state.key !== key, reload: () => setReload((value) => value + 1) };
}

function SharingView({
  requestId,
  item,
  copy,
  locale,
}: {
  requestId: string;
  item: InboxItem | null;
  copy: InboxCopy;
  locale: string;
}) {
  const ws = useWorkspace();
  const { refresh } = useInbox(useInboxKey());
  const read = useSharingRequest(requestId, copy);
  const [busy, setBusy] = useState(false);
  if (read.loading && !read.detail) return <LoadingState label={copy.opening} />;
  if (!read.detail)
    return <ErrorState message={read.error || copy.openFailed} onRetry={read.reload} />;
  const { request, canPublish } = read.detail;
  async function decide(approve: boolean) {
    setBusy(true);
    try {
      await sharingApi.decide(request.id, approve);
      await Promise.all([ws.refresh(), refresh()]);
      ws.notify(approve ? copy.published : copy.rejected);
      read.reload();
    } catch (failure) {
      ws.notify(failure instanceof Error ? failure.message : copy.actionFailed, null, 'error');
    } finally {
      setBusy(false);
    }
  }
  const pending = request.status === 'pending';
  const decided = request.decidedAt ?? request.createdAt;
  return (
    <div className="ws-inbox-canvas">
      <Heading
        meta={
          item
            ? joined(item.actor?.name, dateTimeLabel(item.createdAt, locale))
            : joined(
                request.status === 'approved'
                  ? copy.approved
                  : pending
                    ? copy.pending
                    : copy.declined,
                dateTimeLabel(decided, locale),
              )
        }
        title={item ? itemTitle(item) : request.title || copy.untitled}
        description={item?.detail ? markdownPlainText(item.detail) : null}
      />
      <div className="ws-inbox-canvas-body">
        <ReviewSnapshot request={request} copy={copy} />
      </div>
      {pending && canPublish ? (
        <Footer sentence={copy.pendingFooter}>
          <ActionButton
            variant="quiet"
            data-size="md"
            disabled={busy}
            onClick={() => void decide(false)}
          >
            {copy.decline}
          </ActionButton>
          <ActionButton
            variant="primary"
            data-size="md"
            busy={busy}
            disabled={busy}
            onClick={() => void decide(true)}
          >
            {copy.approveVersion}
          </ActionButton>
        </Footer>
      ) : (
        <Footer
          sentence={
            pending
              ? copy.waitingFooter
              : joined(
                  request.status === 'approved' ? copy.approved : copy.declined,
                  whenLabel(decided, locale),
                )
          }
        />
      )}
    </div>
  );
}

// The exact version the request carries: a Skill's instructions or a Result's text as a
// document, a file or source as its pinned text; the rest behind "Technical details".
function ReviewSnapshot({ request, copy }: { request: SharingRequest; copy: InboxCopy }) {
  const snapshot = request.snapshot ?? {};
  const draft = snapshot.draft as Record<string, unknown> | undefined;
  const payload = request.change?.payload ?? snapshot.payload;
  const markdown = (payload as { markdown?: string } | undefined)?.markdown;
  const instructions = draft?.instructions;
  const preview = snapshot.preview;
  return (
    <>
      <p className="ws-inbox-note">
        {request.status === 'pending' ? copy.submitted : copy.submittedDecided}
      </p>
      {typeof instructions === 'string' || typeof markdown === 'string' ? (
        <div className="ws-inbox-document">
          <WorkspaceMarkdown
            text={String(typeof instructions === 'string' ? instructions : markdown)}
          />
        </div>
      ) : typeof preview === 'string' ? (
        <PinnedSource requestId={request.id} preview={preview} copy={copy} />
      ) : null}
      <details className="ws-inbox-details">
        <summary>{copy.technicalDetails}</summary>
        <pre className="ws-inbox-code">
          {JSON.stringify(
            {
              ...snapshot,
              payload: undefined,
              preview: undefined,
              draft: draft ? { ...draft, instructions: undefined } : undefined,
              change: request.change,
            },
            null,
            2,
          )}
        </pre>
      </details>
    </>
  );
}

function PinnedSource({
  requestId,
  preview,
  copy,
}: {
  requestId: string;
  preview: string;
  copy: InboxCopy;
}) {
  const [page, setPage] = useState<Awaited<ReturnType<typeof sharingApi.content>> | null>(null);
  const [offsets, setOffsets] = useState([0]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function read(offset: number, nextOffsets: number[]) {
    setBusy(true);
    setError('');
    try {
      setPage(await sharingApi.content(requestId, offset));
      setOffsets(nextOffsets);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : copy.openFailed);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="ws-inbox-pinned">
      <pre className="ws-inbox-code">{page?.content ?? preview}</pre>
      <p className="ws-inbox-note">{page ? copy.pageNote : copy.contentStart}</p>
      <div className="ws-actions">
        {!page && (
          <ActionButton
            variant="secondary"
            data-size="md"
            disabled={busy}
            onClick={() => void read(0, [0])}
          >
            {copy.readFull}
          </ActionButton>
        )}
        {offsets.length > 1 && (
          <ActionButton
            variant="secondary"
            data-size="md"
            disabled={busy}
            onClick={() => void read(offsets[offsets.length - 2], offsets.slice(0, -1))}
          >
            {copy.previousPage}
          </ActionButton>
        )}
        {page?.nextOffset !== undefined && page.nextOffset !== null && (
          <ActionButton
            variant="secondary"
            data-size="md"
            disabled={busy}
            onClick={() => void read(page.nextOffset!, [...offsets, page.nextOffset!])}
          >
            {copy.nextPage}
          </ActionButton>
        )}
      </div>
      {error && (
        <p className="ws-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ── The canvas ─────────────────────────────────────────────────────────────────

/** The kind an Inbox canvas shows in its header, and the title its back path names. */
export function useInboxCanvasLabels(ids: (string | null | undefined)[]) {
  const copy = useNamespaceCopy(inboxCopy);
  const extended = useInboxKindLabels();
  const rowMeta = useInboxRowMeta();
  const { inbox } = useInbox(useInboxKey());
  return ids.map((id) => {
    const entry = id ? findInboxEntry(inbox, id) : null;
    if (!entry || entry.section === 'decision')
      return { kind: viewLabels.inbox, title: viewLabels.inbox };
    return {
      kind: kindText(copy, entry.item.kind, extended),
      title:
        rowMeta(entry.item).title ??
        (entry.section === 'update' ? entry.item.title : itemTitle(entry.item)),
    };
  });
}

export function InboxCanvas({ id }: { id: string }) {
  const copy = useNamespaceCopy(inboxCopy);
  const locale = useIntlLocale();
  const { inbox, error, refresh } = useInbox(useInboxKey());
  const entry = findInboxEntry(inbox, id);
  if (entry?.section === 'update')
    return <UpdateView key={id} entry={entry} copy={copy} locale={locale} />;
  const sharing =
    entry && entry.section !== 'decision' && entry.item.ref?.type === 'sharing_request';
  if (entry?.section === 'decision' || sharing)
    return (
      <SharingView
        key={id}
        requestId={entry.section === 'decision' ? entry.requestId : entry.item.ref!.id}
        item={entry.section === 'decision' ? null : entry.item}
        copy={copy}
        locale={locale}
      />
    );
  if (entry) return <ItemView key={id} entry={entry} copy={copy} locale={locale} />;
  if (!inbox)
    return error ? (
      <ErrorState message={error} onRetry={() => void refresh()} />
    ) : (
      <LoadingState label={copy.loading} />
    );
  return (
    <div className="ws-inbox-canvas">
      <Heading meta="" title={copy.gone} description={copy.goneBody} />
    </div>
  );
}
